import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useQuery } from '@tanstack/react-query';
import { X, Funnel, MagnifyingGlass, CaretDown } from '@phosphor-icons/react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { listTeams } from '@/api/teams';
import { listTeamCategories, type TeamCategory } from '@/api/teamCategories';
import { resolve as resolveTeamColor } from '@/lib/teamColor';

// SignupMode mirrors the discriminator on SignupSummaryRow.mode.
export type SignupMode = 'open' | 'admin_approval' | 'waitlist_only';
export type EnabledState = 'open' | 'closed';
export type ActivityKey = 'has_submitted' | 'has_waitlist' | 'has_capacity';

export interface SignupFilters {
  teamIds: string[];
  categoryIds: string[];
  enabledStates: EnabledState[];
  modes: SignupMode[];
  activity: ActivityKey[];
}

export const EMPTY_SIGNUP_FILTERS: SignupFilters = {
  teamIds: [],
  categoryIds: [],
  enabledStates: [],
  modes: [],
  activity: [],
};

export function countActiveSignupFilters(f: SignupFilters): number {
  let n = 0;
  if (f.teamIds.length > 0) n++;
  if (f.categoryIds.length > 0) n++;
  if (f.enabledStates.length > 0) n++;
  if (f.modes.length > 0) n++;
  if (f.activity.length > 0) n++;
  return n;
}

interface Props {
  slug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: SignupFilters;
  onChange: (f: SignupFilters) => void;
}

export function SignupFilterPanel({
  slug,
  open,
  onOpenChange,
  filters,
  onChange,
}: Props) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const activeCount = countActiveSignupFilters(filters);

  const teamsQuery = useQuery({
    queryKey: ['teams', slug],
    queryFn: () => listTeams(token, slug),
    enabled: !!token && !!slug && open,
  });
  const categoriesQuery = useQuery({
    queryKey: ['team-categories', slug],
    queryFn: () => listTeamCategories(token, slug),
    enabled: !!token && !!slug && open,
  });

  function toggleTeam(id: string) {
    const next = filters.teamIds.includes(id)
      ? filters.teamIds.filter((x) => x !== id)
      : [...filters.teamIds, id];
    onChange({ ...filters, teamIds: next });
  }
  function toggleCategory(id: string) {
    const next = filters.categoryIds.includes(id)
      ? filters.categoryIds.filter((x) => x !== id)
      : [...filters.categoryIds, id];
    onChange({ ...filters, categoryIds: next });
  }
  function toggleEnabled(state: EnabledState) {
    const next = filters.enabledStates.includes(state)
      ? filters.enabledStates.filter((s) => s !== state)
      : [...filters.enabledStates, state];
    onChange({ ...filters, enabledStates: next });
  }
  function toggleMode(mode: SignupMode) {
    const next = filters.modes.includes(mode)
      ? filters.modes.filter((m) => m !== mode)
      : [...filters.modes, mode];
    onChange({ ...filters, modes: next });
  }
  function toggleActivity(key: ActivityKey) {
    const next = filters.activity.includes(key)
      ? filters.activity.filter((a) => a !== key)
      : [...filters.activity, key];
    onChange({ ...filters, activity: next });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[22rem]"
      >
        <div className="flex shrink-0 items-center gap-2 border-b border-border px-5 py-4">
          <Funnel className="h-4 w-4 text-muted-foreground" />
          <SheetTitle className="font-heading text-base font-bold">
            {t('signups.filter.title')}
          </SheetTitle>
          {activeCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-pill bg-primary px-1.5 font-heading text-[0.65rem] font-bold text-primary-foreground">
              {activeCount}
            </span>
          )}
          <SheetDescription className="sr-only">
            {t('signups.filter.description')}
          </SheetDescription>
        </div>

        <div className="flex-1 divide-y divide-border overflow-y-auto">
          <FilterSection title={t('signups.filter.teams.title')}>
            <TeamsCombobox
              teams={teamsQuery.data?.teams ?? []}
              loading={teamsQuery.isLoading}
              selectedIds={filters.teamIds}
              onToggle={toggleTeam}
              onClearAll={() => onChange({ ...filters, teamIds: [] })}
            />
          </FilterSection>

          <FilterSection title={t('signups.filter.status.title')}>
            <div className="flex flex-wrap gap-2">
              {(['open', 'closed'] as const).map((state) => (
                <PillToggle
                  key={state}
                  active={filters.enabledStates.includes(state)}
                  onClick={() => toggleEnabled(state)}
                >
                  {t(`signups.filter.status.${state}`)}
                </PillToggle>
              ))}
            </div>
            {filters.enabledStates.length > 0 && (
              <ClearLink
                onClick={() => onChange({ ...filters, enabledStates: [] })}
              />
            )}
          </FilterSection>

          <FilterSection title={t('signups.filter.mode.title')}>
            <div className="flex flex-wrap gap-2">
              {(['open', 'admin_approval', 'waitlist_only'] as const).map(
                (mode) => (
                  <PillToggle
                    key={mode}
                    active={filters.modes.includes(mode)}
                    onClick={() => toggleMode(mode)}
                  >
                    {t(`signups.summary.mode.${mode}`)}
                  </PillToggle>
                ),
              )}
            </div>
            {filters.modes.length > 0 && (
              <ClearLink onClick={() => onChange({ ...filters, modes: [] })} />
            )}
          </FilterSection>

          <FilterSection title={t('signups.filter.activity.title')}>
            <div className="flex flex-wrap gap-2">
              {(['has_submitted', 'has_waitlist', 'has_capacity'] as const).map(
                (key) => (
                  <PillToggle
                    key={key}
                    active={filters.activity.includes(key)}
                    onClick={() => toggleActivity(key)}
                  >
                    {t(`signups.filter.activity.${key}`)}
                  </PillToggle>
                ),
              )}
            </div>
            {filters.activity.length > 0 && (
              <ClearLink onClick={() => onChange({ ...filters, activity: [] })} />
            )}
          </FilterSection>

          <FilterSection title={t('signups.filter.categories.title')}>
            <CategoriesCombobox
              categories={categoriesQuery.data?.categories ?? []}
              loading={categoriesQuery.isLoading}
              selectedIds={filters.categoryIds}
              onToggle={toggleCategory}
              onClearAll={() => onChange({ ...filters, categoryIds: [] })}
            />
          </FilterSection>
        </div>

        {activeCount > 0 && (
          <div className="shrink-0 border-t border-border px-5 py-4">
            <button
              type="button"
              onClick={() => onChange(EMPTY_SIGNUP_FILTERS)}
              className="w-full rounded-pill border border-border py-2 font-heading text-sm font-semibold text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('signups.filter.clearAll')}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function FilterSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 px-5 py-4">
      <h3 className="font-heading text-[0.7rem] font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  );
}

function PillToggle({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-pill border px-3 py-1.5 font-heading text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function ClearLink({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('common');
  return (
    <button
      type="button"
      onClick={onClick}
      className="self-start font-heading text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none"
    >
      {t('signups.filter.clearSection')}
    </button>
  );
}

function TeamsCombobox({
  teams,
  loading,
  selectedIds,
  onToggle,
  onClearAll,
}: {
  teams: { id: string; name: string; color: string; member_count: number }[];
  loading: boolean;
  selectedIds: string[];
  onToggle: (id: string) => void;
  onClearAll: () => void;
}) {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter((tm) => tm.name.toLowerCase().includes(q));
  }, [teams, search]);

  const label =
    selectedIds.length === 0
      ? t('signups.filter.teams.all')
      : t('signups.filter.teams.selected', { count: selectedIds.length });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            selectedIds.length > 0
              ? 'border-primary bg-primary/10 font-heading font-semibold text-foreground'
              : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
          )}
        >
          <span>{loading ? t('signups.filter.loading') : label}</span>
          <CaretDown
            className={cn(
              'h-3.5 w-3.5 shrink-0 transition-transform',
              open && 'rotate-180',
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[18rem] p-0"
        side="bottom"
        sideOffset={4}
      >
        <div className="relative border-b border-border px-3 py-2">
          <MagnifyingGlass className="pointer-events-none absolute left-5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('signups.filter.teams.search')}
            className={cn(
              'w-full bg-transparent py-0.5 pl-6 text-sm placeholder:text-muted-foreground focus:outline-none',
              search.length > 0 && 'pr-6',
            )}
          />
          {search.length > 0 && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label={t('common.clearSearch')}
              className="absolute right-4 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X weight="bold" className="h-3 w-3" />
            </button>
          )}
        </div>
        <div className="max-h-56 overflow-y-auto py-1">
          {filtered.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              {t('signups.filter.teams.none')}
            </p>
          )}
          {filtered.map((team) => {
            const color = resolveTeamColor(team.name, team.color).dot;
            return (
              <label
                key={team.id}
                className="flex cursor-pointer items-center gap-2.5 px-3 py-1.5 hover:bg-muted/50"
              >
                <Checkbox
                  checked={selectedIds.includes(team.id)}
                  onChange={() => onToggle(team.id)}
                />
                <span
                  className="h-2 w-2 shrink-0 rounded-pill"
                  style={{ backgroundColor: color }}
                  aria-hidden
                />
                <span className="flex-1 truncate text-sm text-foreground">
                  {team.name}
                </span>
              </label>
            );
          })}
        </div>
        {selectedIds.length > 0 && (
          <div className="border-t border-border px-3 py-2">
            <button
              type="button"
              onClick={onClearAll}
              className="font-heading text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none"
            >
              {t('signups.filter.clearSection')}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function CategoriesCombobox({
  categories,
  loading,
  selectedIds,
  onToggle,
  onClearAll,
}: {
  categories: TeamCategory[];
  loading: boolean;
  selectedIds: string[];
  onToggle: (id: string) => void;
  onClearAll: () => void;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);

  const label =
    selectedIds.length === 0
      ? t('signups.filter.categories.all')
      : t('signups.filter.categories.selected', { count: selectedIds.length });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex w-full items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            selectedIds.length > 0
              ? 'border-primary bg-primary/10 font-heading font-semibold text-foreground'
              : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
          )}
        >
          <span>{loading ? t('signups.filter.loading') : label}</span>
          <CaretDown
            className={cn(
              'h-3.5 w-3.5 shrink-0 transition-transform',
              open && 'rotate-180',
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[18rem] p-0"
        side="bottom"
        sideOffset={4}
      >
        <div className="max-h-56 overflow-y-auto py-1">
          {categories.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              {t('signups.filter.categories.none')}
            </p>
          )}
          {categories.map((cat) => (
            <label
              key={cat.id}
              className="flex cursor-pointer items-center gap-2.5 px-3 py-1.5 hover:bg-muted/50"
            >
              <Checkbox
                checked={selectedIds.includes(cat.id)}
                onChange={() => onToggle(cat.id)}
              />
              <span className="flex-1 truncate text-sm text-foreground">
                {cat.name}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {cat.team_count}
              </span>
            </label>
          ))}
        </div>
        {selectedIds.length > 0 && (
          <div className="border-t border-border px-3 py-2">
            <button
              type="button"
              onClick={onClearAll}
              className="font-heading text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none"
            >
              {t('signups.filter.clearSection')}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
